import { useState } from 'react';
import { NavLink, Link, Outlet } from 'react-router';
import { ExternalLink, type LucideIcon } from 'lucide-react';
import { Logo } from './Logo';
import { currentTheme, toggleTheme } from '../lib/theme';

export type NavEntry = {
  label: string;
  /** absolute app path, or an absolute URL when external */
  to: string;
  /** leaves the portal — renders a plain anchor into a new tab */
  external?: boolean;
  /** match nested routes too (area roots) */
  end?: boolean;
  badge?: string;
  icon?: LucideIcon;
};

export type NavGroup = {
  label?: string;
  entries: NavEntry[];
};

/** The scope switcher card at the top of the sidebar. */
export type ScopeInfo = {
  restaurant: string;
  store?: string;
  /** where clicking the switcher goes (up one scope) */
  to: string;
};

export function Shell(props: { groups: NavGroup[]; scope?: ScopeInfo; foot: string }) {
  const [theme, setTheme] = useState(currentTheme());
  return (
    <div className="shell">
      <nav className="side">
        <div className="brand">
          <Logo />
        </div>
        {props.scope && (
          <Link className="switcher" to={props.scope.to} title="Switch store">
            <span className="r-name">{props.scope.restaurant}</span>
            {props.scope.store && <span className="s-name">{props.scope.store} ⌄</span>}
          </Link>
        )}
        {props.groups.map((g, i) => (
          <div key={i}>
            {g.label && <div className="nav-group">{g.label}</div>}
            {g.entries.map((e) =>
              e.external ? (
                <a
                  key={e.to}
                  href={e.to}
                  target="_blank"
                  rel="noreferrer"
                  className="nav-item"
                  title={e.to}
                >
                  {e.icon && <e.icon size={16} strokeWidth={2} style={{ marginRight: 10, flex: 'none' }} />}
                  {e.label}
                  <ExternalLink size={13} strokeWidth={2} style={{ marginLeft: 'auto', opacity: 0.5 }} />
                </a>
              ) : (
                <NavLink
                  key={e.to}
                  to={e.to}
                  end={e.end ?? false}
                  className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                >
                  {e.icon && <e.icon size={16} strokeWidth={2} style={{ marginRight: 10, flex: 'none' }} />}
                  {e.label}
                  {e.badge && <span className="badge">{e.badge}</span>}
                </NavLink>
              ),
            )}
          </div>
        ))}
        <div className="nav-foot">
          {props.foot}
          <br />
          <button
            className="theme-toggle"
            onClick={() => setTheme(toggleTheme())}
            aria-label="Toggle theme"
          >
            ◐ {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
        </div>
      </nav>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
